import { getTopObstructionHeight } from '~/composables/useTopObstructionHeight'

interface VisibleSectionSyncOptions {
  sectionIds: Readonly<Ref<string[]>>
  strategy?: 'center' | 'top-marker'
  topMarkerOffset?: number
  onChange: (sectionId: string | undefined) => void
}

interface VisibleSectionEntry {
  sectionId: string
  rect: DOMRect
}

function getScrollContainer() {
  const containers = inject(diKeys.containers, null)
  return computed(() => containers?.content?.value ?? null)
}

export function useVisibleSectionSync(options: VisibleSectionSyncOptions) {
  const {
    sectionIds,
    strategy = 'center',
    topMarkerOffset = 80,
    onChange,
  } = options

  const scrollContainer = getScrollContainer()
  const visibleSectionId = ref<string | undefined>()

  function getEffectiveTop(containerEl: HTMLElement, containerRect: DOMRect) {
    const stickyHeight = getTopObstructionHeight(containerEl, containerRect)
    return {
      stickyHeight,
      effectiveTop: containerRect.top + stickyHeight,
    }
  }

  function findClosestVisibleSection(containerEl: HTMLElement): string | undefined {
    const containerRect = containerEl.getBoundingClientRect()
    const { stickyHeight, effectiveTop } = getEffectiveTop(containerEl, containerRect)

    const visibleEntries = sectionIds.value
      .map((sectionId) => {
        const element = document.getElementById(sectionId)
        if (!element) {
          return null
        }

        const rect = element.getBoundingClientRect()
        if (rect.bottom < effectiveTop || rect.top > containerRect.bottom) {
          return null
        }

        return { sectionId, rect } as VisibleSectionEntry
      })
      .filter((entry): entry is VisibleSectionEntry => entry !== null)

    if (!visibleEntries.length) {
      return undefined
    }

    if (strategy === 'top-marker') {
      const markerY = effectiveTop + Math.max(topMarkerOffset, stickyHeight)

      return visibleEntries.reduce<{ sectionId: string | undefined, distance: number }>((closest, entry) => {
        const distance = Math.abs(entry.rect.top - markerY)
        if (distance < closest.distance) {
          return { sectionId: entry.sectionId, distance }
        }
        return closest
      }, { sectionId: undefined, distance: Infinity }).sectionId
    }

    const centerY = effectiveTop + (containerRect.bottom - effectiveTop) / 2

    return visibleEntries.reduce<{ sectionId: string | undefined, distance: number }>((closest, entry) => {
      const elementCenterY = entry.rect.top + entry.rect.height / 2
      const distance = Math.abs(elementCenterY - centerY)
      if (distance < closest.distance) {
        return { sectionId: entry.sectionId, distance }
      }
      return closest
    }, { sectionId: undefined, distance: Infinity }).sectionId
  }

  function syncVisibleSectionFromScroll() {
    const containerEl = scrollContainer.value
    if (!containerEl || !sectionIds.value.length) {
      return
    }

    const sectionId = findClosestVisibleSection(containerEl)
    if (sectionId === visibleSectionId.value) {
      return
    }

    visibleSectionId.value = sectionId
    onChange(sectionId)
  }

  watch(scrollContainer, (containerEl, _prev, onCleanup) => {
    if (!containerEl) {
      return
    }

    containerEl.addEventListener('scroll', syncVisibleSectionFromScroll, { passive: true })
    nextTick(() => syncVisibleSectionFromScroll())

    onCleanup(() => {
      containerEl.removeEventListener('scroll', syncVisibleSectionFromScroll)
    })
  }, { immediate: true })

  watch(() => sectionIds.value.join('|'), () => {
    nextTick(() => syncVisibleSectionFromScroll())
  })

  return {
    syncVisibleSectionFromScroll,
  }
}
